import React from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useInView } from '../hooks/useInView';
import { Send, Phone, Mail, MapPin, Clock } from 'lucide-react';

const contactInfo = [
  { icon: Phone, label: 'Telefoon', value: '+31 (0) 6 38 07 68 89', href: null, phone: true },
  { icon: Mail, label: 'E-mail', value: 'info@ferryinfra.nl', href: 'mailto:info@ferryinfra.nl' },
  { icon: MapPin, label: 'Locatie', value: 'Nederland', href: null },
  { icon: Clock, label: 'Bereikbaar', value: 'Ma - Vr: 07:00 - 17:00', href: null },
];

export default function Contact() {
  const [ref, inView] = useInView({ threshold: 0.1 });
  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm();

  const onSubmit = async (data) => {
    try {
      const formData = new URLSearchParams();
      formData.append('form-name', 'contact');
      formData.append('naam', data.name);
      formData.append('email', data.email);
      formData.append('phone', data.phone || '');
      formData.append('onderwerp', data.subject);
      formData.append('bericht', data.message);

      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });

      if (response.ok) {
        toast.success('Bericht succesvol verzonden! Wij nemen spoedig contact met u op.');
        reset();
      } else {
        toast.error('Er is iets misgegaan. Probeer het opnieuw.');
      }
    } catch (error) {
      toast.error('Er is iets misgegaan. Probeer het opnieuw.');
    }
  };

  return (
    <section id="contact" ref={ref} className="section-padding relative overflow-hidden bg-ferry-darker">
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-ferry-crimson/30 to-transparent" />

      {/* Background accents */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-ferry-crimson/5 rounded-full blur-[200px]" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-ferry-crimson/3 rounded-full blur-[200px]" />

      <div className="max-w-7xl mx-auto relative">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-ferry-crimson font-semibold text-sm uppercase tracking-widest">Contact</span>
          <h2 className="text-3xl md:text-4xl font-bold mt-4 mb-6">
            Neem <span className="gradient-text">contact</span> met ons op
          </h2>
          <p className="text-ferry-gray-text max-w-2xl mx-auto leading-relaxed">
            Heeft u een project of vraag? Wij staan klaar om u te helpen. Vul het formulier in 
            of neem direct contact met ons op.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-12">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-2 space-y-6"
          >
            <div className="glass-card p-8 space-y-8">
              <div>
                <h3 className="text-xl font-bold text-white mb-2">Contactgegevens</h3>
                <p className="text-ferry-gray-text text-sm leading-relaxed">
                  Wij reageren doorgaans binnen 24 uur op uw bericht.
                </p>
              </div>

              <div className="space-y-6">
                {contactInfo.map((item) => (
                  <div key={item.label} className="flex items-start gap-4 group">
                    <div className="w-10 h-10 rounded-xl bg-ferry-crimson/10 flex items-center justify-center flex-shrink-0 group-hover:bg-ferry-crimson/20 transition-colors duration-300">
                      <item.icon size={18} className="text-ferry-crimson" />
                    </div>
                    <div>
                      <p className="text-xs text-ferry-gray-text uppercase tracking-wider mb-1">{item.label}</p>
                      {item.phone ? (
                        <button
                          type="button"
                          onClick={() => { window.location.href = ['tel:', '+31', '638', '076', '889'].join(''); }}
                          className="text-white hover:text-ferry-crimson transition-colors duration-200 text-sm font-medium cursor-pointer bg-transparent border-none p-0 text-left"
                        >
                          {item.value}
                        </button>
                      ) : item.href ? (
                        <a href={item.href} className="text-white hover:text-ferry-crimson transition-colors duration-200 text-sm font-medium">
                          {item.value}
                        </a>
                      ) : (
                        <p className="text-white text-sm font-medium">{item.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Decorative */}
              <div className="pt-6 border-t border-white/5">
                <p className="text-ferry-cream text-sm italic">"Veilig werken aan de energie van morgen"</p>
              </div>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="lg:col-span-3"
          >
            <form onSubmit={handleSubmit(onSubmit)} className="glass-card p-8 space-y-6" data-netlify="true" data-netlify-honeypot="bot-field">
              <input type="hidden" name="form-name" value="contact" />
              <p className="hidden">
                <label>Don't fill this out: <input name="bot-field" /></label>
              </p>
              <div className="grid sm:grid-cols-2 gap-6">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-ferry-gray-text mb-2">
                    Naam <span className="text-ferry-crimson">*</span>
                  </label>
                  <input
                    {...register('name', { required: 'Naam is verplicht' })}
                    type="text"
                    placeholder="Uw volledige naam"
                    className={`w-full px-4 py-3 bg-white/5 border rounded-xl text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-ferry-crimson/50 focus:border-ferry-crimson/50 transition-all duration-300 text-sm ${
                      errors.name ? 'border-red-500/50' : 'border-white/10'
                    }`}
                  />
                  {errors.name && (
                    <p className="mt-1.5 text-xs text-red-400">{errors.name.message}</p>
                  )}
                </div>

                {/* Company */}
                <div>
                  <label className="block text-sm font-medium text-ferry-gray-text mb-2">
                    Bedrijf
                  </label>
                  <input
                    {...register('company')}
                    type="text"
                    placeholder="Uw bedrijfsnaam"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-ferry-crimson/50 focus:border-ferry-crimson/50 transition-all duration-300 text-sm"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-ferry-gray-text mb-2">
                    E-mail <span className="text-ferry-crimson">*</span>
                  </label>
                  <input
                    {...register('email', {
                      required: 'E-mail is verplicht',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Ongeldig e-mailadres',
                      },
                    })}
                    type="email"
                    placeholder="uw@email.nl"
                    className={`w-full px-4 py-3 bg-white/5 border rounded-xl text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-ferry-crimson/50 focus:border-ferry-crimson/50 transition-all duration-300 text-sm ${
                      errors.email ? 'border-red-500/50' : 'border-white/10'
                    }`}
                  />
                  {errors.email && (
                    <p className="mt-1.5 text-xs text-red-400">{errors.email.message}</p>
                  )}
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-ferry-gray-text mb-2">
                    Telefoon
                  </label>
                  <input
                    {...register('phone')}
                    type="tel"
                    placeholder="+31 6 1234 5678"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-ferry-crimson/50 focus:border-ferry-crimson/50 transition-all duration-300 text-sm"
                  />
                </div>
              </div>

              {/* Subject */}
              <div>
                <label className="block text-sm font-medium text-ferry-gray-text mb-2">
                  Onderwerp <span className="text-ferry-crimson">*</span>
                </label>
                <select
                  {...register('subject', { required: 'Selecteer een onderwerp' })}
                  className={`w-full px-4 py-3 bg-white/5 border rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-ferry-crimson/50 focus:border-ferry-crimson/50 transition-all duration-300 text-sm appearance-none cursor-pointer ${
                    errors.subject ? 'border-red-500/50' : 'border-white/10'
                  }`}
                  defaultValue=""
                >
                  <option value="" disabled className="bg-ferry-dark">Selecteer een onderwerp</option>
                  <option value="offerte" className="bg-ferry-dark">Offerte aanvragen</option>
                  <option value="samenwerking" className="bg-ferry-dark">Samenwerking</option>
                  <option value="informatie" className="bg-ferry-dark">Algemene informatie</option>
                  <option value="vacature" className="bg-ferry-dark">Vacatures / Sollicitatie</option>
                  <option value="anders" className="bg-ferry-dark">Anders</option>
                </select>
                {errors.subject && (
                  <p className="mt-1.5 text-xs text-red-400">{errors.subject.message}</p>
                )}
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-ferry-gray-text mb-2">
                  Bericht <span className="text-ferry-crimson">*</span>
                </label>
                <textarea
                  {...register('message', {
                    required: 'Bericht is verplicht',
                    minLength: { value: 10, message: 'Bericht moet minimaal 10 tekens bevatten' },
                  })}
                  rows={5}
                  placeholder="Beschrijf uw vraag of project..."
                  className={`w-full px-4 py-3 bg-white/5 border rounded-xl text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-ferry-crimson/50 focus:border-ferry-crimson/50 transition-all duration-300 text-sm resize-none ${
                    errors.message ? 'border-red-500/50' : 'border-white/10'
                  }`}
                />
                {errors.message && (
                  <p className="mt-1.5 text-xs text-red-400">{errors.message.message}</p>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full sm:w-auto px-8 py-4 bg-ferry-crimson hover:bg-ferry-crimson-light disabled:bg-ferry-crimson/50 text-white font-semibold rounded-xl transition-all duration-300 hover:shadow-xl hover:shadow-ferry-crimson/25 hover:-translate-y-0.5 disabled:hover:translate-y-0 disabled:cursor-not-allowed flex items-center justify-center gap-3"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Verzenden...
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    Bericht Versturen
                  </>
                )}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
